import { useQuery, useQueryClient } from "@tanstack/react-query";
import { transactionApi } from "../../api";
import type { SavedPaymentMethodResponse } from "../../types/generated";
import {
  assertPaymentMethodDetachBarrierGenerationCurrent,
  capturePaymentMethodDetachBarrierGeneration,
  reconcilePaymentMethods,
} from "./paymentMethodDetachBarrier";
import { paymentQueryKeys } from "./paymentQueryKeys";

export interface PaymentMethodsQueryOptions {
  refetchOnReconnect?: boolean;
  refetchOnWindowFocus?: boolean;
  captureRequestEpoch?: () => number | null;
  onRequestSuccess?: (requestEpoch: number | null) => void;
}

/**
 * The user's SAVED cards at a facility wallet
 * (GET /transactions/facilities/{id}/payment-methods).
 *
 * Facility-routed: the query is keyed by facilityId for authorization and
 * request ownership, but sibling facilities can alias one organization-owned
 * Stripe account. Each response carries connected_account_id so detach safety
 * is shared by the real wallet. The chosen payment_method_id is what the
 * facility charge (booking / enrollment / tournament entry) uses.
 *
 * Shared spine (promoted out of modules/booking into @bw/shared). Default
 * behaviour is byte-identical to the old booking-local hook: gated on a truthy
 * facilityId AND the caller's `enabled` flag, with `retry: false`, and keyed
 * under the `["booking","payment-methods",facilityId]` slot so every
 * facility-routed surface shares one cache slot per facility. A caller that
 * owns stricter request-generation fencing may disable automatic focus or
 * reconnect refetches and observe the epoch owned by each actual request
 * without changing the defaults for neighboring flows.
 */
export function usePaymentMethods(
  facilityId: string | undefined,
  enabled = true,
  queryOptions: PaymentMethodsQueryOptions = {},
) {
  const { captureRequestEpoch, onRequestSuccess, ...refetchPolicy } =
    queryOptions;
  const queryClient = useQueryClient();
  const queryKey = paymentQueryKeys.paymentMethods(facilityId ?? "");

  return useQuery<SavedPaymentMethodResponse[]>({
    queryKey,
    queryFn: async () => {
      const authGeneration = capturePaymentMethodDetachBarrierGeneration();
      const requestEpoch = captureRequestEpoch?.() ?? null;
      const paymentMethods = await transactionApi.listPaymentMethods(
        facilityId as string,
      );
      assertPaymentMethodDetachBarrierGenerationCurrent(authGeneration);
      onRequestSuccess?.(requestEpoch);
      return reconcilePaymentMethods(
        queryClient,
        queryKey,
        paymentMethods,
        authGeneration,
      );
    },
    enabled: Boolean(facilityId) && enabled,
    retry: false,
    ...refetchPolicy,
  });
}
